import { useState, useEffect } from 'react';
import {
  Users, School, BookOpen, ChevronLeft, Bell, BellOff, X, ChevronRight,
  Home, CalendarDays, Settings, Activity, AlertTriangle, FileText,
  CheckCircle, XCircle, Clock, BarChart3, Shield
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../api/client';
import { useNavigate } from 'react-router-dom';
import { UserAvatar } from '../../components/ui/UserAvatar';
import './responsavel-mobile.css';
import { ROUTES } from '../../api/routes';
import { useResponsavelId } from './useResponsavelId';

interface Filho {
  vinculoId: string;
  principal: boolean;
  recebeNotificacao: boolean;
  grauParentesco: string | null;
  aluno: {
    id: string;
    nome: string;
    matricula: string;
    fotoUrl?: string | null;
    escola?: { id: string; nome: string };
    turma?: { id: string; nome: string; serie: string; turno: string };
  };
  presencaHoje?: {
    statusPresencaDiaria: string;
    totalPresencas: number;
    totalAusencias: number;
    diasPresentes?: number;
    diasAusentes?: number;
    frequenciaPct?: number;
  } | null;
}

const STATUS_MAP: Record<string, { label: string; color: string; bg: string; icon: typeof CheckCircle }> = {
  PRESENTE: { label: 'Presente Hoje', color: '#16a34a', bg: '#dcfce7', icon: CheckCircle },
  AUSENTE: { label: 'Ausente Hoje', color: '#dc2626', bg: '#fecaca', icon: XCircle },
  ATRASADO: { label: 'Atrasado', color: '#f59e0b', bg: '#fef3c7', icon: Clock },
  PARCIAL: { label: 'Parcial', color: '#f59e0b', bg: '#fef3c7', icon: AlertTriangle },
  SAIU: { label: 'Saiu cedo', color: '#6b7280', bg: '#e5e7eb', icon: AlertTriangle },
  INCONSISTENTE: { label: 'Inconsistente', color: '#dc2626', bg: '#fecaca', icon: AlertTriangle },
  INDEFINIDO: { label: 'Aguardando Chamada', color: '#64748b', bg: '#f1f5f9', icon: Clock },
};

const PARENTESCO_LABEL: Record<string, string> = {
  PAI: 'Pai',
  MAE: 'Mãe',
  AVO: 'Avô/Avó',
  TIO: 'Tio/Tia',
  IRMAO: 'Irmão/Irmã',
  OUTRO: 'Outro',
};

function useIsMobile(bp = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < bp);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [bp]);
  return isMobile;
}

export function MeusFilhos() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [filhos, setFilhos] = useState<Filho[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilho, setSelectedFilho] = useState<Filho | null>(null);
  const [responsavelInfo, setResponsavelInfo] = useState<{ nome: string; email?: string | null } | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const responsavelId = useResponsavelId();

  // Fallback: se o hook não resolveu responsavelId do JWT, usa user.id como última tentativa
  const idParaBusca = responsavelId || (user?.id ?? '');

  useEffect(() => {
    if (!idParaBusca) {
      setLoading(false);
      setFetchError('responsavelId não encontrado no token JWT. Faça logout e login novamente.');
      return;
    }

    setFetchError(null);
    setLoading(true);

    Promise.all([
      api.get(ROUTES.mobileResponsavel.resumo(idParaBusca)),
      api.get(ROUTES.mobileResponsavel.statusHoje(idParaBusca)).catch(() => ({ data: [] })),
    ])
      .then(([resumoRes, statusRes]) => {
        // Captura info do responsável para exibir na mensagem de vazio
        const respInfo = resumoRes.data?.responsavel;
        if (respInfo) setResponsavelInfo(respInfo);

        const alunosList: Filho[] = resumoRes.data?.alunos || [];
        const statusList = Array.isArray(statusRes.data) ? statusRes.data : [];

        // Mesclar dados de status hoje e presenca nos alunos
        const merged = alunosList.map((item) => {
          const matchStatus = statusList.find((s: any) => s.aluno?.id === item.aluno?.id || s.vinculoId === item.vinculoId);
          return {
            ...item,
            aluno: {
              ...item.aluno,
              fotoUrl: matchStatus?.aluno?.fotoUrl || item.aluno?.fotoUrl,
            },
            presencaHoje: matchStatus?.presencaHoje || null,
          };
        });

        setFilhos(merged);
      })
      .catch((err: any) => {
        const status = err?.response?.status;
        const apiMsg =
          err?.response?.data?.error?.message || err?.response?.data?.message;
        if (apiMsg) {
          setFetchError(`${apiMsg} (HTTP ${status || 'erro'})`);
        } else if (status === 403 || status === 401) {
          setFetchError(`Acesso não autorizado (${status}). ID: ${idParaBusca}`);
        } else if (status === 404) {
          setFetchError(`Responsável não encontrado no banco (404). ID: ${idParaBusca}`);
        } else {
          setFetchError(`Erro ao carregar filhos (${status || 'rede'}). ID: ${idParaBusca}`);
        }
      })
      .finally(() => setLoading(false));
  }, [idParaBusca]);

  if (!isMobile) {
    const pushAtivos = filhos.filter(f => f.recebeNotificacao).length;
    const principais = filhos.filter(f => f.principal).length;

    return (
      <div className="admin-page" style={{ maxWidth: 1400, width: '100%', margin: '0 auto', paddingBottom: 32 }}>
        {/* ═══ Standardized Hero Banner ═══ */}
        <div style={{
          background: 'radial-gradient(135% 140% at 100% 0%, #312e81 0%, #1e1b4b 55%, #0f172a 100%)',
          borderRadius: 20,
          padding: '20px 28px',
          color: '#ffffff',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 22,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ minWidth: 260, position: 'relative', zIndex: 2 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
              padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
              color: '#e0e7ff', marginBottom: 6,
            }}>
              <Users size={12} color="#818cf8" /> Núcleo Familiar • Aluno Conectado
            </div>
            <h1 style={{ fontSize: '1.45rem', fontWeight: 800, margin: 0, color: '#ffffff', letterSpacing: '-0.02em' }}>
              Meus Filhos & Dependentes
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'rgba(255,255,255,0.72)' }}>
              Ficha viva com presença em tempo real, rendimento escolar, alertas e acesso rápido
            </p>
          </div>

          {/* Hero KPIs */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', position: 'relative', zIndex: 2 }}>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#38bdf8', lineHeight: 1.1 }}>{filhos.length}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Filhos</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#4ade80', lineHeight: 1.1 }}>{pushAtivos}/{filhos.length}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Push Ativo</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#f472b6', lineHeight: 1.1 }}>{principais}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Principal</div>
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>Carregando dados dos dependentes...</div>
        ) : fetchError ? (
          <div style={{ textAlign: 'center', padding: 48, background: '#fff5f5', borderRadius: 16, border: '1px solid #fecaca' }}>
            <Shield size={40} style={{ color: '#ef4444', margin: '0 auto 16px' }} />
            <h3 style={{ margin: '0 0 8px', color: '#b91c1c', fontSize: '1rem', fontWeight: 700 }}>Erro ao carregar filhos</h3>
            <p style={{ color: '#64748b', fontSize: '0.82rem', margin: 0, fontFamily: 'monospace', wordBreak: 'break-all' }}>{fetchError}</p>
          </div>
        ) : filhos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0' }}>
            <Users size={48} style={{ color: '#cbd5e1', margin: '0 auto 16px' }} />
            <h3 style={{ margin: '0 0 8px', color: '#0f172a', fontSize: '1.1rem', fontWeight: 700 }}>Nenhum vínculo encontrado</h3>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 16px', margin: '12px auto', maxWidth: 340, textAlign: 'left' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6, letterSpacing: '0.05em' }}>Responsável autenticado</div>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a' }}>{responsavelInfo?.nome || user?.nome || '—'}</div>
              <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>{responsavelInfo?.email || user?.email || '—'}</div>
              {user?.cpf && <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 2 }}>CPF: {user.cpf}</div>}
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 4, fontFamily: 'monospace' }}>ID buscado: {idParaBusca || '(vazio)'}</div>
            </div>
            <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0 }}>
              Não encontramos alunos vinculados a este responsável. Verifique com a secretaria da escola se o vínculo foi realizado corretamente.
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 20 }}>
            {filhos.map((f) => {
              const totalP = f.presencaHoje?.totalPresencas || 0;
              const totalF = f.presencaHoje?.totalAusencias || 0;
              const pct = typeof f.presencaHoje?.frequenciaPct === 'number'
                ? f.presencaHoje.frequenciaPct
                : (totalP + totalF > 0 ? Math.round((totalP / (totalP + totalF)) * 100) : 100);
              const stDia = STATUS_MAP[f.presencaHoje?.statusPresencaDiaria || ''] || STATUS_MAP.INDEFINIDO;
              const StatusIcon = stDia.icon;

              return (
                <div key={f.vinculoId} style={{
                  background: '#ffffff', borderRadius: 20, border: '1px solid #e2e8f0', padding: 22,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', gap: 16,
                  transition: 'all 0.2s',
                }}>
                  {/* Header do Card */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <UserAvatar
                        src={f.aluno.fotoUrl}
                        name={f.aluno.nome}
                        role="ALUNO"
                        size={48}
                      />
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <h3 style={{ margin: 0, fontSize: '1.08rem', fontWeight: 800, color: '#0f172a' }}>{f.aluno.nome}</h3>
                          {f.principal && (
                            <span style={{ fontSize: '0.62rem', fontWeight: 800, color: '#2563eb', background: '#eff6ff', border: '1px solid #dbeafe', padding: '2px 8px', borderRadius: 999 }}>
                              PRINCIPAL
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: 3 }}>
                          Matrícula: <strong>{f.aluno.matricula || 'N/A'}</strong> • {PARENTESCO_LABEL[f.grauParentesco || ''] || f.grauParentesco || 'Família'}
                        </div>
                      </div>
                    </div>

                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      color: f.recebeNotificacao ? '#16a34a' : '#94a3b8',
                      background: f.recebeNotificacao ? '#f0fdf4' : '#f8fafc',
                      border: `1px solid ${f.recebeNotificacao ? '#bbf7d0' : '#e2e8f0'}`,
                      padding: '4px 10px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700,
                    }}>
                      {f.recebeNotificacao ? <Bell size={12} /> : <BellOff size={12} />}
                      <span>{f.recebeNotificacao ? 'PUSH ATIVO' : 'SEM PUSH'}</span>
                    </div>
                  </div>

                  {/* Status de Presença de Hoje ao Vivo */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: stDia.bg,
                    color: stDia.color,
                    padding: '10px 14px',
                    borderRadius: 12,
                    fontWeight: 700,
                    fontSize: '0.82rem',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <StatusIcon size={18} />
                      <span>{stDia.label}</span>
                    </div>
                    <span style={{ fontSize: '0.72rem', opacity: 0.85, fontWeight: 600 }}>Registro em tempo real</span>
                  </div>

                  {/* Info Box Acadêmica */}
                  <div style={{ background: '#f8fafc', borderRadius: 14, padding: 14, display: 'flex', flexDirection: 'column', gap: 10, border: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', color: '#334155' }}>
                      <School size={16} color="#64748b" />
                      <span style={{ fontWeight: 600 }}>{f.aluno.escola?.nome || 'Escola não vinculada'}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', color: '#334155' }}>
                      <BookOpen size={16} color="#64748b" />
                      <span>{f.aluno.turma?.nome || 'Sem turma'} {f.aluno.turma?.turno ? `• Turno ${f.aluno.turma.turno}` : ''}</span>
                    </div>
                  </div>

                  {/* Indicadores do Mês */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, background: '#ffffff', border: '1px solid #f1f5f9', borderRadius: 14, padding: '10px 14px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Presenças</div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#16a34a', marginTop: 2 }}>{totalP}</div>
                    </div>
                    <div style={{ textAlign: 'center', borderLeft: '1px solid #f1f5f9', borderRight: '1px solid #f1f5f9' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Faltas</div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 800, color: totalF > 0 ? '#dc2626' : '#64748b', marginTop: 2 }}>{totalF}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Freq. Mês</div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 800, color: pct >= 75 ? '#16a34a' : '#ea580c', marginTop: 2 }}>{pct}%</div>
                    </div>
                  </div>

                  {/* Quick Action Buttons */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 'auto', paddingTop: 6 }}>
                    <button
                      onClick={() => navigate('/responsavel/desempenho')}
                      style={{
                        background: '#ffffff', border: '1px solid #e2e8f0', color: '#334155',
                        padding: '9px 10px', borderRadius: 10, fontSize: '0.78rem', fontWeight: 600,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                      }}
                    >
                      <BarChart3 size={14} color="#3b82f6" /> Boletim
                    </button>
                    <button
                      onClick={() => navigate('/responsavel/frequencia')}
                      style={{
                        background: '#ffffff', border: '1px solid #e2e8f0', color: '#334155',
                        padding: '9px 10px', borderRadius: 10, fontSize: '0.78rem', fontWeight: 600,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                      }}
                    >
                      <CalendarDays size={14} color="#10b981" /> Frequência
                    </button>
                    <button
                      onClick={() => navigate('/responsavel/justificativas')}
                      style={{
                        background: '#eff6ff', border: '1px solid #bfdbfe', color: '#2563eb',
                        padding: '9px 10px', borderRadius: 10, fontSize: '0.78rem', fontWeight: 700,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                      }}
                    >
                      <FileText size={14} color="#2563eb" /> Justificar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="resp-mobile-root">
      {/* ═══ NAVY HEADER (Compact) ═══ */}
      <div className="resp-navy-header" style={{ padding: 'max(env(safe-area-inset-top, 40px), 40px) 20px 30px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: 'white', letterSpacing: '-0.3px' }}>
            Meus Filhos
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', fontWeight: 400 }}>
            {filhos.length} aluno(s) vinculado(s)
          </p>
        </div>
      </div>

      {/* ═══ CONTENT AREA ═══ */}
      <div className="resp-content-area" style={{ paddingBottom: 40 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: '0.82rem', fontWeight: 600 }}>Carregando vínculos...</div>
        ) : fetchError ? (
          <div className="resp-child-card" style={{ textAlign: 'center', padding: '32px 20px' }}>
            <Shield size={36} style={{ color: '#ef4444', margin: '0 auto 12px' }} />
            <h3 style={{ margin: '0 0 8px', color: '#b91c1c', fontSize: '0.95rem', fontWeight: 600 }}>Erro ao carregar</h3>
            <p style={{ color: '#64748b', fontSize: '0.75rem', margin: 0, lineHeight: 1.4, fontFamily: 'monospace', wordBreak: 'break-all' }}>{fetchError}</p>
          </div>
        ) : filhos.length === 0 ? (
          <div className="resp-child-card" style={{ textAlign: 'center', padding: '36px 20px' }}>
            <Users size={40} style={{ color: '#cbd5e1', margin: '0 auto 14px' }} />
            <h3 style={{ margin: '0 0 6px', color: '#0f172a', fontSize: '1rem', fontWeight: 600 }}>Nenhum vínculo</h3>
            {/* Identificação do responsável buscado */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', margin: '10px 0 12px', textAlign: 'left' }}>
              <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4, letterSpacing: '0.05em' }}>Responsável autenticado</div>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#0f172a' }}>{responsavelInfo?.nome || user?.nome || '—'}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>{responsavelInfo?.email || user?.email || '—'}</div>
              {user?.cpf && <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 2 }}>CPF: {user.cpf}</div>}
            </div>
            <p style={{ color: '#64748b', fontSize: '0.78rem', margin: 0, lineHeight: 1.4, fontWeight: 400 }}>
              Nenhum aluno vinculado encontrado. Procure a secretaria da escola.
            </p>
          </div>
        ) : (
          <div style={{ marginTop: 8 }}>
            <div className="resp-section-label" style={{ marginBottom: 8, paddingLeft: 4 }}>Alunos vinculados:</div>
            <div style={{ background: 'white', borderRadius: '16px', padding: '0 14px', boxShadow: '0 1px 8px rgba(0,0,0,0.04), 0 0 1px rgba(0,0,0,0.06)' }}>
              {filhos.map((f, idx) => (
                <div key={f.vinculoId} onClick={() => setSelectedFilho(f)} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0',
                  borderBottom: idx < filhos.length - 1 ? '1px solid #f1f5f9' : 'none',
                  cursor: 'pointer'
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 600, fontSize: '0.9rem', color: '#0369a1',
                    flexShrink: 0
                  }}>
                    {f.aluno.nome.charAt(0)}
                  </div>
                  
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {f.aluno.nome}
                      </span>
                      {f.principal && <span style={{ fontSize: '0.5rem', fontWeight: 600, color: '#10b981', background: '#ecfdf5', padding: '2px 6px', borderRadius: 10 }}>PRINCIPAL</span>}
                    </div>
                    <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 400 }}>
                      {f.aluno.turma?.nome || 'Sem turma'} · {f.aluno.escola?.nome || 'Escola não vinculada'}
                    </div>
                  </div>
                  
                  <ChevronRight size={16} style={{ color: '#cbd5e1', flexShrink: 0 }} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ═══ MODAL DETALHES DO ALUNO ═══ */}
      {selectedFilho && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20, zIndex: 9999,
          animation: 'fadeIn 0.2s ease-out'
        }} onClick={() => setSelectedFilho(null)}>
          <div style={{
            background: 'white', borderRadius: 24, width: '100%', maxWidth: 400,
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
            overflow: 'hidden', position: 'relative',
            animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
          }} onClick={e => e.stopPropagation()}>
            
            {/* Modal Header */}
            <div style={{ background: 'radial-gradient(120% 120% at 100% 0%, #2563eb 0%, #0b1526 100%)', padding: '16px 20px', position: 'relative', overflow: 'hidden' }}>
              <button onClick={() => setSelectedFilho(null)} style={{
                position: 'absolute', top: 12, right: 12, background: 'rgba(255,255,255,0.15)', border: 'none',
                width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', cursor: 'pointer', zIndex: 10
              }}>
                <X size={16} />
              </button>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', zIndex: 2 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 600, fontSize: '1.4rem', color: '#0369a1',
                  border: '2px solid white',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  flexShrink: 0
                }}>
                  {selectedFilho.aluno.nome.charAt(0)}
                </div>
                <div style={{ flex: 1, minWidth: 0, paddingRight: 20 }}>
                  <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600, color: 'white', letterSpacing: '-0.2px', lineHeight: 1.2 }}>
                    {selectedFilho.aluno.nome}
                  </h3>
                  <p style={{ margin: '2px 0 0', fontSize: '0.7rem', color: 'rgba(255,255,255,0.8)', fontWeight: 400, letterSpacing: '0.5px' }}>
                    MATRÍCULA: <span style={{ color: 'white', fontWeight: 600 }}>{selectedFilho.aluno.matricula || 'N/A'}</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{ padding: 16 }}>
              {/* Academic Info */}
              <div style={{ background: '#f8fafc', borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 10, border: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <School size={16} style={{ color: '#64748b' }} />
                  <div style={{ flex: 1, fontSize: '0.8rem', fontWeight: 500, color: '#0f172a' }}>
                    {selectedFilho.aluno.escola?.nome || 'Escola não informada'}
                  </div>
                </div>
                
                <div style={{ height: 1, background: '#e2e8f0', marginLeft: 26 }}></div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <BookOpen size={16} style={{ color: '#64748b' }} />
                  <div style={{ flex: 1, fontSize: '0.8rem', fontWeight: 500, color: '#0f172a' }}>
                    {selectedFilho.aluno.turma?.nome || 'Sem turma'} <span style={{ color: '#64748b', fontWeight: 400 }}>— {selectedFilho.aluno.turma?.serie}</span>
                  </div>
                </div>
              </div>

              {/* Quick Stats Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                <div style={{ background: '#d1fae5', borderRadius: 12, padding: '16px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#047857', marginBottom: 6 }}>
                    <Activity size={14} strokeWidth={2.5} />
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Frequência</span>
                  </div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#064e3b', lineHeight: 1 }}>—</div>
                  <div style={{ fontSize: '0.6rem', color: '#047857', marginTop: 4 }}>Ver detalhes</div>
                </div>
                <div style={{ background: '#fee2e2', borderRadius: 12, padding: '16px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#b91c1c', marginBottom: 6 }}>
                    <AlertTriangle size={14} strokeWidth={2.5} />
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Faltas</span>
                  </div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#7f1d1d', lineHeight: 1 }}>—</div>
                  <div style={{ fontSize: '0.6rem', color: '#b91c1c', marginTop: 4 }}>Ver detalhes</div>
                </div>
              </div>

              {/* Quick Actions */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8, marginTop: 16 }}>
                <button onClick={() => navigate('/responsavel/frequencia')} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
                  padding: '10px 0', color: '#0f172a', fontWeight: 500, fontSize: '0.85rem',
                  cursor: 'pointer'
                }}>
                  <FileText size={16} style={{ color: '#3b82f6' }} /> Ver Boletim / Relatório
                </button>
              </div>

              {/* Footer Meta */}
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginTop: 16, paddingTop: 12, borderTop: '1px solid #f1f5f9'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {selectedFilho.principal ? (
                    <span style={{ fontSize: '0.6rem', fontWeight: 600, color: '#10b981', background: '#ecfdf5', padding: '4px 10px', borderRadius: 12, letterSpacing: '0.5px' }}>
                      PRINCIPAL
                    </span>
                  ) : (
                    <span style={{ fontSize: '0.6rem', fontWeight: 600, color: '#64748b', background: '#f1f5f9', padding: '4px 10px', borderRadius: 12 }}>
                      SECUNDÁRIO
                    </span>
                  )}
                  <span style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 500 }}>
                    • {PARENTESCO_LABEL[selectedFilho.grauParentesco || ''] || selectedFilho.grauParentesco || 'Família'}
                  </span>
                </div>
                
                {selectedFilho.recebeNotificacao ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#10b981', background: '#ecfdf5', padding: '4px 10px', borderRadius: 12 }}>
                    <Bell size={12} strokeWidth={2} />
                    <span style={{ fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.5px' }}>ATIVO</span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#94a3b8', background: '#f1f5f9', padding: '4px 10px', borderRadius: 12 }}>
                    <BellOff size={12} strokeWidth={2} />
                    <span style={{ fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.5px' }}>OFF</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>
      {/* ═══ TAB BAR ═══ */}
      <div className="resp-tab-bar">
        {[
          { key: 'home', icon: Home, label: 'Inicio', to: '/responsavel/dashboard' },
          { key: 'filhos', icon: Users, label: 'Filhos', to: '/responsavel/filhos' },
          { key: 'freq', icon: CalendarDays, label: 'Frequencia', to: '/responsavel/frequencia' },
          { key: 'avisos', icon: Bell, label: 'Avisos', to: '/responsavel/comunicados' },
          { key: 'perfil', icon: Settings, label: 'Perfil', to: '/responsavel/perfil' },
        ].map((tab) => (
          <button key={tab.key}
            className={`resp-tab-item ${tab.key === 'filhos' ? 'active' : ''}`}
            onClick={() => navigate(tab.to)}>
            <div style={{ position: 'relative' }}>
              <tab.icon size={20} />
            </div>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

    </div>
  );
}
